import { cmsGraphQLClient } from '@app/graphQLClient';
import { useParsePaginationFromUrl } from '@app/hooks/useParsePaginationFromUrl';
import { BoatsCardsList } from '@components/BoatsCardsList';
import Jumbotron from '@components/Jumbotron';
import { Layout } from '@components/Layout';
import { MetaDataInterface } from '@interfaces/metaData';
import { dehydrate } from '@tanstack/react-query';
import { Breadcrumb } from 'antd';
import { DEFAULT_PER_PAGE, TWO_MINUTES_IN_MILLISECONDS } from 'mdc-constants';
import { GetStaticPaths, GetStaticProps } from 'next';
import { FC, useMemo } from 'react';
import {
  GetManufacturerBoatsVariables,
  GetManufacturersBySlugVariables,
  Manufacturer,
  useGetManufacturerBoats,
  useGetManufacturersBySlug,
} from 'services/graphql/cms';
import { useManufacturerBoats } from 'services/hooks/queries/useManufacturerBoats';
import { queryClient } from 'services/queryClient';

interface PageProps {
  manufacturer: Manufacturer;
}

const ManufacturerBoats: FC<PageProps> = ({ manufacturer }) => {
  const { name: title, description, seo } = manufacturer;

  const metaData: MetaDataInterface = {
    title: title,
    description: seo?.metaDescription,
  };

  const { currentPage, currentPerPage, orderBy } = useParsePaginationFromUrl();

  const sort = useMemo<string[]>(() => {
    if (orderBy === 'MOST_VIEWED') {
      return ['views_count:desc'];
    }

    if (orderBy === 'A_TO_Z') {
      return ['year_first:desc', 'model:desc'];
    }

    return ['createdAt:desc'];
  }, [orderBy]);

  const publicBoatsVariables: GetManufacturerBoatsVariables = {
    manufacturerId: manufacturer.documentId,
    page: currentPage,
    pageSize: currentPerPage,
    sort,
  };

  const { data, isFetching, isPlaceholderData } = useManufacturerBoats(publicBoatsVariables);

  const isSpinning = useMemo(() => isFetching && isPlaceholderData, [isFetching, isPlaceholderData]);

  return (
    <Layout meta={metaData}>
      <>
        <Jumbotron
          breadCrumb={[0]}
          pageName="maker"
          title={title}
          desc={description}
          breadCrumbChildren={
            <>
              <Breadcrumb.Item>{title}</Breadcrumb.Item>
            </>
          }
        />
        <BoatsCardsList boats={data?.boats_connection} isSpinning={isSpinning} />
      </>
    </Layout>
  );
};

export const getServerSideProps: GetStaticProps = async (context) => {
  const slug: string = context.params.slug as string;

  const variables: GetManufacturersBySlugVariables = {
    slug,
  };

  const fetchedManufacturers = await useGetManufacturersBySlug.fetcher(cmsGraphQLClient, variables)();

  if (fetchedManufacturers.manufacturers_connection.nodes.length) {
    const [manufacturer] = fetchedManufacturers.manufacturers_connection.nodes;

    const publicBoatsVariables: GetManufacturerBoatsVariables = {
      manufacturerId: manufacturer.documentId,
      page: 1,
      pageSize: DEFAULT_PER_PAGE,
      sort: ['createdAt:desc'],
    };

    await queryClient.prefetchQuery({
      queryKey: ['getManufacturerBoats', publicBoatsVariables],
      queryFn: useGetManufacturerBoats.fetcher(cmsGraphQLClient, publicBoatsVariables),
      staleTime: TWO_MINUTES_IN_MILLISECONDS,
      gcTime: TWO_MINUTES_IN_MILLISECONDS,
    });

    return {
      props: {
        manufacturer,
        dehydratedState: dehydrate(queryClient),
      },
    };
  }

  return {
    notFound: true,
  };
};

// export const getStaticPaths: GetStaticPaths = async () => {
//   const variables: GetManufacturersVariables = {
//     limit: 1000,
//   };

//   const manufacturersData: GetManufacturers = await useGetManufacturers.fetcher(cmsGraphQLClient, variables)();

//   if (manufacturersData?.manufacturers?.data?.length) {
//     const paths = manufacturersData.manufacturers.data.map((item) => ({
//       params: { slug: item?.attributes?.slug },
//     }));

//     return {
//       paths,
//       fallback: false,
//     };
//   }

//   return {
//     paths: [],
//     fallback: true,
//   };
// };

export default ManufacturerBoats;
